import { createEnvironmentInjector, EnvironmentInjector, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { HxConfirmationPopup, HxDialogService } from '@complexcare/harmony';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ImpersonationService } from '../services/impersonation.service';

/** Configuration options for the unsaved changes confirmation dialog */
export interface UnsavedChangesConfig {
  /** Dialog header text */
  headerText?: string;
  /** Confirmation message body */
  message?: string;
  /** Accept button label */
  acceptLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Dialog variant (visual style) */
  variant?: 'warning' | 'error' | 'info';
}

/**
 * Interface for components that can have unsaved changes. Components must implement this to work with the unsaved
 * changes guard.
 */
export interface HasUnsavedChanges {
  /** Returns true if the component has unsaved changes that need confirmation before leaving */
  hasUnsavedChanges(): boolean;

  /** Optional: Custom confirmation message. Defaults to generic message if not provided. */
  getUnsavedChangesMessage?(): string;

  /** Optional: Custom dialog configuration. When provided, allows customization of all dialog aspects. */
  getUnsavedChangesConfig?(): Partial<UnsavedChangesConfig>;

  /**
   * Optional: Called after user confirms leaving. Deactivate auto-save and persist if needed; navigation waits for
   * completion.
   */
  onConfirmedDeactivate?(): Observable<void> | Promise<void> | void;
}

/**
 * Functional CanDeactivate guard for preventing navigation when there are unsaved changes.
 *
 * Shows a confirmation dialog before allowing navigation away from a component with unsaved changes.
 *
 * Default config:
 *
 * - HeaderText: 'Unsaved Changes'
 * - Message: 'You have unsaved changes. Are you sure you want to leave? Your progress will be lost.'
 * - AcceptLabel: 'Leave'
 * - CancelLabel: 'Stay'
 * - Variant: 'warning'
 *
 * @example
 *   // Basic usage with defaults
 *   ```typescript
 *   import { unsavedChangesGuard, HasUnsavedChanges } from '@complexcare/conductor';
 *
 *   @Component({...})
 *   export class MyComponent implements HasUnsavedChanges {
 *     readonly hasChanges = signal<boolean>(false);
 *
 *     hasUnsavedChanges(): boolean {
 *       return this.hasChanges();
 *     }
 *   }
 *
 *   // In routes:
 *   {
 *     path: 'form',
 *     component: MyComponent,
 *     canDeactivate: [unsavedChangesGuard],
 *   }
 *   ```;
 *
 * @example
 *   // Customized dialog with getUnsavedChangesConfig
 *   ```typescript
 *   export class MyComponent implements HasUnsavedChanges {
 *     readonly hasChanges = signal<boolean>(false);
 *
 *     hasUnsavedChanges(): boolean {
 *       return this.hasChanges();
 *     }
 *
 *     getUnsavedChangesConfig(): Partial<UnsavedChangesConfig> {
 *       return {
 *         headerText: 'Discard Changes?',
 *         message: 'Your form data will be lost. Continue?',
 *         acceptLabel: 'Discard',
 *         cancelLabel: 'Keep Editing',
 *         variant: 'error',
 *       };
 *     }
 *   }
 *   ```;
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (
  component: HasUnsavedChanges
): boolean | Observable<boolean> => {
  // Bypass guard if currently ending impersonation session
  const impersonationService = inject(ImpersonationService, { optional: true });
  if (impersonationService?.isEndingImpersonation() || (window as any).__symphonyIsEndingImpersonation) {
    return true;
  }

  // If no unsaved changes, allow navigation
  if (!component.hasUnsavedChanges()) {
    return true;
  }

  // Default configuration
  const defaultConfig: UnsavedChangesConfig = {
    headerText: 'Unsaved Changes',
    message: 'You have unsaved changes. Are you sure you want to leave? Your progress will be lost.',
    acceptLabel: 'Leave',
    cancelLabel: 'Stay',
    variant: 'warning',
  };

  // Merge custom config with defaults
  const customConfig = component.getUnsavedChangesConfig?.() ?? {};
  const config: UnsavedChangesConfig = { ...defaultConfig, ...customConfig };

  // For backward compatibility: if only getUnsavedChangesMessage is provided, use it
  const message = component.getUnsavedChangesMessage?.() || config.message;

  // HxDialogService is not providedIn:'root', so we create a child environment
  // injector scoped to this guard invocation to host it.
  const parentInjector = inject(EnvironmentInjector);
  const guardInjector = createEnvironmentInjector([HxDialogService], parentInjector);
  const dialogService = guardInjector.get(HxDialogService);

  const dialogRef = dialogService.open(HxConfirmationPopup, {
    inputs: {
      headerText: config.headerText,
      message,
      variant: config.variant,
      showAcceptButton: true,
      acceptLabel: config.acceptLabel,
      showCancelButton: true,
      cancelLabel: config.cancelLabel,
    },
    size: 'medium',
    ariaLabel: 'Unsaved changes confirmation',
  });

  // Wire up accept/cancel to close with result
  const instance = (dialogRef as any).componentInstance as HxConfirmationPopup;
  if (instance) {
    instance.accept.subscribe(() => dialogRef.close(true));
    instance.cancel.subscribe(() => dialogRef.close(false));
  }

  // Map dialog result: only true (accept) allows navigation
  // X button, cancel, or any other close returns false (prevent navigation)
  return dialogRef.afterClosed().pipe(
    switchMap((result) => {
      if (result !== true) return of(false);
      const cleanup = component.onConfirmedDeactivate?.();
      return (cleanup ? from(cleanup) : of(undefined)).pipe(map(() => true));
    })
  );
};
